// Two-way transcript <-> caption sync via SHARED source word IDs.
// Caption objects reference transcript words by word_id (source_word_ids). A visual
// duplicate references the SAME source word_id — it never creates a new spoken word.
// When the transcript changes (text edit / re-align), caption objects update:
//   - text  refreshes from the current source words   (unless object.textLocked)
//   - timing refreshes from the current source words   (unless object.timeLocked)
// Objects whose source words were all deleted are removed.

export function syncCaptionsFromTranscript(segments, layers, toTl = null) {
  if (!layers || !layers.length) return layers;
  const wordMap = {};
  (segments || []).forEach((s) => (s.words || []).forEach((w) => { if (w.word_id) wordMap[w.word_id] = w; }));

  return layers.map((layer) => {
    const objects = (layer.objects || [])
      .map((o) => {
        // User-edited caption text is authoritative — never let a transcript re-sync
        // overwrite or revert it. (Its own words were kept in lock-step at edit time.)
        if (o.textLocked) return o;
        const srcIds = (o.words || []).map((w) => w.word_id).filter(Boolean);
        if (!srcIds.length) return o; // no source reference (e.g. free text) — leave as-is
        if (o.timeLocked) {
          // keep the object's own timing/placement; only refresh visible word text
          const words = (o.words || []).map((w) => (wordMap[w.word_id] ? { ...w, word: wordMap[w.word_id].word } : w));
          const next = { ...o, words };
          if (!o.textLocked) next.text = words.map((w) => w.word).join(" ");
          return next;
        }
        const present = srcIds.map((id) => wordMap[id]).filter(Boolean);
        if (!present.length) return null; // all source words gone → drop the caption
        const words = present.map((w) => {
          if (!toTl) return { word_id: w.word_id, word: w.word, start: w.start, end: w.end };
          const m = toTl(w.start, w.end);
          return m ? { word_id: w.word_id, word: w.word, ...m } : null; // null = word was cut out of the edited video
        }).filter(Boolean);
        if (!words.length) return null;
        const next = { ...o, words };
        if (!o.textLocked) next.text = words.map((w) => w.word).join(" ");
        next.start = Math.min(...words.map((w) => w.start));
        next.end = Math.max(...words.map((w) => w.end));
        return next;
      })
      .filter(Boolean);
    return { ...layer, objects };
  });
}

export const layersEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
